import {
  decapsulateSecurePayload,
  decryptPrivateKey,
} from "@/lib/crypto/crypto";

/**
 * In-memory caching layer for expensive crypto operations.
 *
 * Purpose:
 * - Avoid re-decrypting the user's private keys (userPK) on every message.
 * - Avoid re-decapsulating the chat secret key (csk) for the same chat.
 *
 * How it works:
 * - Keys are cached per-tab only (not persisted across reloads).
 * - userPK is cached for the current authenticated user.
 * - csk is cached per (user, eCSK) pair in a bounded Map with pruning.
 *
 * Key points:
 * - All inputs/outputs are hex strings (no raw keys leaked to callers).
 * - Cache is cleared explicitly on logout via clearCryptoCache().
 * - Map size is kept under control via simple LRU-style pruning.
 */

// Approximate lifetimes; meant as safety bounds, not correctness guarantees.
const USER_PK_TTL_MS = 30 * 60_000; // 30 minutes
const CSK_TTL_MS = 2 * 60 * 60_000; // 2 hours

// Hard cap on number of cached CSKs per tab. This controls memory growth even
// if a user jumps between many chats in one session.
const MAX_CSK_CACHE_SIZE = 200;

type UserPKCacheKey = string;

type UserPKCacheEntry = {
  key: UserPKCacheKey;
  userPK: string;
  expiresAt: number;
};

type CSKCacheEntry = {
  csk: string;
  userKey: UserPKCacheKey;
  eCSK: string;
  // When this entry becomes invalid even if the map is not pruned yet.
  expiresAt: number;
  // Used for simple LRU-style pruning when the cache is over capacity.
  lastUsedAt: number;
};

// Single active user per tab is the common case, so a single-entry cache is
// sufficient for userPK.
let userPKCache: UserPKCacheEntry | null = null;

// Per-chat CSK cache (bounded + pruned).
const cskCache = new Map<string, CSKCacheEntry>();

const makeUserKey = (params: {
  username: string;
  dk: string;
  ePrK: string;
}): UserPKCacheKey => {
  // If any of these change (e.g. account switch, key rotation), the computed
  // key also changes and the old cached entry is ignored.
  return `${params.username}:${params.dk}:${params.ePrK}`;
};

const makeCSKKey = (eCSK: string, userKey: UserPKCacheKey): string =>
  `${eCSK}:${userKey}`;

/**
 * Remove expired entries and, if needed, prune least-recently-used ones
 * to enforce MAX_CSK_CACHE_SIZE.
 */
const pruneCSKCacheIfNeeded = (now: number): void => {
  // 1) Drop expired entries first.
  for (const [key, entry] of cskCache) {
    if (entry.expiresAt <= now) {
      cskCache.delete(key);
    }
  }

  // 2) Enforce hard capacity using a simple LRU strategy based on lastUsedAt.
  if (cskCache.size <= MAX_CSK_CACHE_SIZE) {
    return;
  }

  const entries = Array.from(cskCache.entries());
  entries.sort((a, b) => a[1].lastUsedAt - b[1].lastUsedAt);

  const excess = cskCache.size - MAX_CSK_CACHE_SIZE;
  for (let i = 0; i < excess; i += 1) {
    const [keyToDelete] = entries[i];
    cskCache.delete(keyToDelete);
  }
};

/**
 * Get the decrypted user private keys (userPK) for the current user,
 * using an in-memory cache when possible.
 */
export const getOrDecryptUserPK = async (params: {
  username: string;
  dk: string;
  ePrK: string;
}): Promise<string> => {
  const key = makeUserKey(params);
  const now = Date.now();

  if (userPKCache && userPKCache.key === key && userPKCache.expiresAt > now) {
    return userPKCache.userPK;
  }

  const userPK = await decryptPrivateKey(params.ePrK, params.dk);

  userPKCache = {
    key,
    userPK,
    expiresAt: now + USER_PK_TTL_MS,
  };

  return userPK;
};

/**
 * Get the chat secret key (csk) for a given (user, eCSK) pair, using the
 * in-memory cache when possible.
 */
export const getOrDecapsulateCSK = async (params: {
  username: string;
  dk: string;
  ePrK: string;
  eCSK: string;
}): Promise<string> => {
  const userKey = makeUserKey(params);
  const now = Date.now();
  const cacheKey = makeCSKKey(params.eCSK, userKey);

  // First, clean up and prune the cache based on current time and size.
  pruneCSKCacheIfNeeded(now);

  const cached = cskCache.get(cacheKey);
  if (cached && cached.expiresAt > now) {
    cached.lastUsedAt = now;
    return cached.csk;
  }

  const userPK = await getOrDecryptUserPK({
    username: params.username,
    dk: params.dk,
    ePrK: params.ePrK,
  });

  const csk = decapsulateSecurePayload({
    privateKeys: userPK,
    securePayload: params.eCSK,
  });

  cskCache.set(cacheKey, {
    csk,
    userKey,
    eCSK: params.eCSK,
    expiresAt: now + CSK_TTL_MS,
    lastUsedAt: now,
  });

  return csk;
};

/**
 * Clear all cached crypto material for this tab.
 *
 * Intended usage:
 * - Call this on logout or when the authenticated user changes.
 */
export const clearCryptoCache = (): void => {
  userPKCache = null;
  cskCache.clear();
};
